import { useState } from "react";

import { Button } from "@aidron/aidron-ds";

import { CustomText, CustomHeading } from "../../StyledComponents/styleComponents.index";

import { 
  NavButton,
  CarouselTrack,
  CarouselSlide,
  ProductCard,
  Dot
} from "../../../modules/PurchaseManagementModule/SalesRecomendation/components/SalesRecommendationTableOptions/salesRecommendationTableOptions.style"

import PropTypes from "prop-types";
import { i18n } from "../../../translate/i18n";


const { t } = i18n;

const ProductsCarousel = ({ updatedProductsList }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const ITEMS_PER_SLIDE = 4;
  const totalSlides = Math.ceil(updatedProductsList.length / ITEMS_PER_SLIDE);
  
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex + 1 >= totalSlides ? 0 : prevIndex + 1
    );
  };
  
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex - 1 < 0 ? totalSlides - 1 : prevIndex - 1
    );
  };
  
  const goToSlide = (index) => setCurrentIndex(index);
  
  const getProductSlides = () => {
    const slides = [];
    for (let i = 0; i < updatedProductsList.length; i += ITEMS_PER_SLIDE) {
      slides.push(updatedProductsList.slice(i, i + ITEMS_PER_SLIDE));
    }
    return slides;
  };
  
  const productSlides = getProductSlides();
  const currentSlide = currentIndex + 1;
  const startItem = (currentIndex * ITEMS_PER_SLIDE) + 1;
  const endItem = Math.min(startItem + ITEMS_PER_SLIDE - 1, updatedProductsList.length);
  
  if (updatedProductsList.length === 0) return <div>Nenhum produto encontrado.</div>
  
  return (
    <section style={{ inlineSize: '100%', position: 'relative', marginBlockEnd: '0.8rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingInline: '2rem' }}>
        <p style={{ color: '#666', fontSize: '0.8rem' }}>
          Mostrando {startItem}-{endItem} de {updatedProductsList.length} produtos
        </p>
        
        {totalSlides > 1 && (
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <NavButton onClick={prevSlide} disabled={currentIndex === 0}>
              ← Anterior
            </NavButton>

            <span>{currentSlide}/{totalSlides}</span>

            <NavButton onClick={nextSlide} disabled={currentIndex === totalSlides - 1}>
              Próximo →
            </NavButton>
          </div>
        )}
      </div>
      
      <div style={{ overflow: 'hidden', borderRadius: '0.5rem', padding: '1rem' }}>
        <CarouselTrack currentIndex={currentIndex}>
          {productSlides.map((slide) => (
            <CarouselSlide key={slide}>
              {slide.map((product) => (
                <ProductCard key={product.productId}>
                  <div style={{ borderBlockEnd: '1px solid #5f5fe5e', paddingBlockEnd: '0.75rem', }}>
                    {product.productName && 
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
                        {`${t("product")}: ${product.productName}`}
                      </CustomText>}
                    {product.subgroup && 
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold"> 
                        {`${t("subgroup")}: ${product.subgroup}`}
                      </CustomText>}                      
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {typeof product.effectivePurchase !== 'undefined' && (
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
                        {`${t("effectivePurchase")}: ${product.effectivePurchase}`}
                      </CustomText>
                    )}

                    {product.leadTime && 
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
                        {`${t("leadTime_pt1")} ${t("leadTime_pt2")}: ${product.leadTime}`}
                      </CustomText>}

                    {product.supplierName && 
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
                        {`${t("supplier")}: ${product.supplierName}`}
                      </CustomText>}                      

                    {product.minStock && 
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
                        {`${t("minimumInventory")}: ${product.minStock}`}
                      </CustomText>}

                    {product.packToUnitRatio && 
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
                        {`${t("conversionFactor")}: ${product.packToUnitRatio}`}
                      </CustomText>}                      

                    {product.prefixName && 
                      <CustomText color={"mediumGrey"} fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
                        {`${t("code")}: ${product.prefixName}`}
                      </CustomText>}                  
                  </div>
                </ProductCard>
              ))}
            </CarouselSlide>
          ))}
        </CarouselTrack>
      </div>
      
      {totalSlides > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '05.rem', marginBlockStart: '1.2rem' }}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <Dot
              key={index}
              active={index === currentIndex}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export const SuccessModal = ({ actions, ctx, uiState }) => {

  const { setShowSuccessModal } = actions || {};
  const { 
    t, 
    updatedProductsList,
    blockedProductsList,
    dataToDisplayToTenantAdmin
  } = ctx || {};
  const { isPrefixOfMeasurePage = false, isSettingsPage = false } = uiState || {};

  const overlayStyle = {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(2px)',
  };

  const modalStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  if(isPrefixOfMeasurePage) {
    return (
      <div style={overlayStyle}>
        <div style={{ ...modalStyle, inlineSize: "80%" }}>
          <CustomHeading size="sm" margin="0 0 16px">
            {t("prefixesUpdateSuccessMessage")}
          </CustomHeading>

          <Button
            label={t("close")}
            type="filled"
            onClick={() => setShowSuccessModal(false)}
          />
        </div>
      </div>
    );    
  };

  if (isSettingsPage) {
    return (
      <div style={overlayStyle}>
        <div style={{ ...modalStyle, inlineSize: "80%" }}>
          <CustomHeading size="sm" margin="0 0 16px">
            {'Usuário adicionado'}
          </CustomHeading>

          <div>
            <p>Novo usuário na plataforma:</p>
            <ul>
              <li>
                {`Nome: ${dataToDisplayToTenantAdmin?.username || ""}`}
              </li>

              <li>
                {`Email: ${dataToDisplayToTenantAdmin?.email || ""}`}
              </li>

              <li>
                {`Hierarquia: ${dataToDisplayToTenantAdmin?.role || ""}`}
              </li>

              {dataToDisplayToTenantAdmin?.stores?.map((store) => {
                return (
                  <li>
                    {`Loja: ${store || ""}`}
                  </li>
                )
              })}

            </ul>
          </div>

          <Button
            label={t("close")}
            type="filled"
            onClick={() => setShowSuccessModal(false)}
          />
        </div>
      </div>
    );      
  }

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, inlineSize: "80%" }}>
        <CustomHeading size="sm" margin="0 0 16px">
          {t("successTitle")}
        </CustomHeading>

        <ProductsCarousel updatedProductsList={updatedProductsList} />

        {blockedProductsList?.length > 0 && (
          <div style={{ inlineSize: "fit-content" }}>
            <p style={{ color: "#e75d0d", fontWeight: "bold", fontSize: '1rem' }}>
              {`Produto(s) que não puderam ser atualizados pois já integram um pedido:`}
            </p>

            {!!blockedProductsList[0]?.orderId && 
              <p style={{ fontSize: "0.85rem", color: "#333", marginBottom: "0.5rem" }}>
                {`Número do pedido: ${blockedProductsList[0]?.orderId}`}
              </p>
            }

            <ul style={{ color: "#333" }}>
              {blockedProductsList.map(item => (
                <li style={{ fontSize: "0.8rem" }} key={item.productId}>
                  {item.productName}
                </li>
              ))}
            </ul>
          </div>
        )}     

        <Button
          label={t("close")}
          type="filled"
          onClick={() => setShowSuccessModal(false)}
        />
      </div>
    </div>
  );
};

SuccessModal.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
}

ProductsCarousel.propTypes = {
  updatedProductsList: PropTypes.array
};